import { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Grid2 } from '@mui/material';
import { AutoCompleteField } from './FormFields/AutoCompleteField';
import { useCityList, useCountryList, useStateList, useZoneList } from '../../assets/api/useGetApi';
import { TextFormField } from './FormFields/TextFormField';
import { useDebounce } from '../hook/useDebounce';

export const LocationSelectorField = ({
    countryField,
    stateField,
    cityField,
    zipField,
    Input_Width,
    passedCountryId,
    passedStateId,
    isShow,
    isZone = false,
    zoneField,
    isStoredId = false
}) => {
    const [searchCity, setSearchCity] = useState("")
    const [searchState, setSearchState] = useState("")

    const debouncedSearchCity = useDebounce(searchCity, 500);
    const debouncedStateCity = useDebounce(searchState, 500);
    const [pincodeStateName, setPincodeStateName] = useState(null);
    const [pincodeCityName, setPincodeCityName] = useState(null);
    const [countryId, setCountryId] = useState(passedCountryId);
    const [stateId, setStateId] = useState(passedStateId);
    const [cityId, setCityId] = useState();




    const { data: countryList, isLoading: countryLoading } = useCountryList();
    const { data: stateList, isLoading: stateLoading } = useStateList({ country_id: countryId, search: debouncedStateCity });
    const { data: cityList, isLoading: cityLoading } = useCityList({
        state_id: stateId,
        country_id: countryId,
        search: debouncedSearchCity
    });

    const countryOption = countryList?.data?.map((item) => ({
        id: item?.id,
        label: item?.country_name,
        value: item?.country_name,
        country_id: item?.id
    }));

    const stateOption = stateList?.data?.map((item) => ({
        id: item?.id,
        label: item?.state_name,
        value: item?.state_name,
        state_id: item?.id
    }));

    const cityOption = cityList?.data?.map((item) => ({
        id: item?.id,
        label: item?.city_name,
        value: item?.city_name,
        city_id: item?.id
    }));

    const { data: ZoneList, isLoading: isLoadingZone } = useZoneList({ city_id: cityId });
    const ZoneOption = ZoneList?.data?.map((item) => ({
        id: item?.id,
        value: item?.zone_name,
        label: item?.zone_name
    })) || [];

    const { setFieldValue, setFieldError, values } = useFormikContext();

    // ✅ STEP 1: Sync stateId after state list loads
    useEffect(() => {
        if (stateList?.data?.length && pincodeStateName) {
            const foundState = stateList.data.find(
                (s) => s.state_name.toLowerCase() === pincodeStateName.toLowerCase()
            );
            if (foundState) {
                setStateId(foundState.id);
                if (isStoredId) {
                    setFieldValue(stateField, foundState.id);
                } else {
                    setFieldValue(stateField, foundState.state_name);
                }
            }
        }
    }, [stateList, pincodeStateName,]);

    // ✅ STEP 2: Sync cityId after city list loads
    useEffect(() => {
        if (cityList?.data?.length && pincodeCityName) {
            const foundCity = cityList.data.find(
                (c) => c.city_name.toLowerCase() === pincodeCityName.toLowerCase()
            );
            if (foundCity) {
                setCityId(foundCity.id);
                if (isStoredId) {
                    setFieldValue(cityField, foundCity.id);
                } else {
                    setFieldValue(cityField, foundCity.city_name);
                }
            }
        }
    }, [cityList, pincodeCityName,]);

    const handlePincodeChange = async (pincode) => {
        setFieldValue(zipField, pincode);

        if (pincode.length === 6) {
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
                const data = await res.json();
                if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length) {
                    const postOffice = data[0].PostOffice[0];
                    const countryName = postOffice.Country;
                    const stateName = postOffice.State;
                    const cityName = postOffice.District || postOffice.Name;

                    setPincodeStateName(stateName);
                    setPincodeCityName(cityName);
                    setSearchState(stateName)
                    setSearchCity(cityName)


                    // Set country immediately
                    const selectedCountry = countryOption?.find(
                        (c) => c?.value.toLowerCase() === countryName.toLowerCase()
                    );

                    if (isStoredId) {
                        setFieldValue(countryField, selectedCountry?.id || '');
                    } else {
                        setFieldValue(countryField, countryName || '');
                    }

                    if (selectedCountry) setCountryId(selectedCountry.country_id);

                    setFieldError(zipField, "");
                } else {
                    setPincodeStateName("")
                    setPincodeCityName("")
                    setFieldError(zipField, "Invalid Pincode");
                    setFieldValue(cityField, "");
                    setFieldValue(stateField, "");
                    setFieldValue(countryField, "");
                    setFieldValue(zoneField, "");
                }
            } catch {
                setFieldError(zipField, "Unable to validate pincode");
            }
        } else {
            setPincodeStateName("")
            setPincodeCityName("")
            setFieldError(zipField, "");
            setFieldValue(cityField, "");
            setFieldValue(stateField, "");
            setFieldValue(countryField, "");
            setFieldValue(zoneField, "");
        }
    };

    return (
        <>
            <Grid2 children size={{ xs: 12, sm: 6 }}>
                <Field
                    name={zipField}
                    component={TextFormField}
                    label="Pincode"
                    isRequired
                    onChange={(e) => handlePincodeChange(e)}
                />
            </Grid2>

            <Grid2 children size={Input_Width}>
                <Field
                    name={countryField}
                    component={AutoCompleteField}
                    options={countryOption}
                    label="Country"
                    isRequired
                    isLoading={countryLoading}
                    onValueChange={(value) => setCountryId(value?.country_id)}
                    nooptionText={"No Country found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                />
            </Grid2>

            <Grid2 children size={Input_Width}>
                <Field
                    name={stateField}
                    component={AutoCompleteField}
                    options={stateOption}
                    label="State"
                    isRequired
                    isLoading={stateLoading}
                    onValueChange={(value) => setStateId(value?.state_id)}
                    nooptionText={"No State found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                    onSearchChange={(val) => {
                        setSearchState(val)
                    }}
                    onClear={() => {
                        setSearchState("");
                        setSearchCity("")
                    }}

                />
            </Grid2>

            <Grid2 children size={Input_Width}>
                <Field
                    name={cityField}
                    component={AutoCompleteField}
                    options={cityOption}
                    label="City"
                    isRequired
                    isLoading={cityLoading}
                    onValueChange={(value) => setCityId(value?.city_id)}
                    nooptionText={"No City Found"}
                    isShow={isShow}
                    isStoredId={isStoredId}
                    onSearchChange={(val) => {
                        setSearchCity(val)
                    }}
                    onClear={() => {
                        setSearchCity("")
                    }}
                />
            </Grid2>

            {isZone && (
                <Grid2 children size={Input_Width}>
                    <Field
                        name={zoneField}
                        component={AutoCompleteField}
                        options={ZoneOption}
                        label="Select Zone"
                        isRequired
                        isLoading={isLoadingZone}
                        isStoredId={true}
                    />
                </Grid2>
            )}
        </>
    );
};
